// =============================================================================
// Consent handling for advertising & measurement tags (Meta Pixel, TikTok Pixel)
// -----------------------------------------------------------------------------
// Default posture: denied in regions that require consent, until the visitor
// decides. Outside those regions tracking stays enabled and no banner is shown.
// The country is read from the same-origin /cdn-cgi/trace endpoint; a failure,
// a non-OK response, an unknown country (XX) or Tor (T1) all count as
// unresolved and the banner is shown.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";

export const META_PIXEL_ID = "2202812660651985";
export const META_PIXEL_ID_2 = "1229912286017561";
export const TIKTOK_PIXEL_ID = "DB1CN6RC77UA626EEHS0";

const CONSENT_KEY = "sabitrade_consent_v1";
const VISITOR_ID_KEY = "sabitrade_visitor_id";
const COUNTRY_KEY = "sabitrade_region_country";

/** Bump this whenever NOTICE_TEXT changes. Every stored choice records which
 *  notice it was given against, so the wording shown stays retrievable. */
export const NOTICE_VERSION = "2026-10-07.1";

/** The exact notice shown in the banner. Stored choices resolve to this text. */
export const NOTICE_TEXT =
  "We use cookies and similar technology to measure visits to SabiTradeNG and how " +
  "well our advertising performs on Meta (Facebook and Instagram) and TikTok. Those " +
  "platforms are only loaded once you accept, and nothing is counted before then. You " +
  "can refuse, or change your mind later from Cookie settings. Your name, email and " +
  "phone number are never shared with them for advertising without your consent.";

/** EEA, UK, EEA-EFTA and Switzerland — the regions where advertising cookies
 *  need the visitor's say-so before they run. */
const CONSENT_REGIONS = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE", "GB", "NO", "IS", "LI", "CH",
]);

export type ConsentChoice = "accepted" | "rejected";

export interface ConsentRecord {
  /** Which notice the visitor saw when they decided. */
  noticeVersion: string;
  /** null = never asked, because the region does not require a choice. */
  choice: ConsentChoice | null;
  region: "consent_required" | "open";
  country: string;
  decidedAt: string | null;
  recordedAt: string;
  /** Acceptance evidence survives a later withdrawal. */
  previouslyAcceptedAt?: string;
  previouslyAcceptedVersion?: string;
}

export const CONSENT_CHANGED_EVENT = "sabitrade:consent-changed";
export const COOKIE_SETTINGS_EVENT = "sabitradeng:cookie-settings";

// ---------------------------------------------------------------------------
// Region lookup — cached for the page visit
// ---------------------------------------------------------------------------

function cacheCountry(country: string): void {
  try {
    sessionStorage.setItem(COUNTRY_KEY, country);
  } catch {
    /* storage unavailable — the in-memory value below still applies */
  }
}

function cachedCountry(): string {
  try {
    return sessionStorage.getItem(COUNTRY_KEY) || "XX";
  } catch {
    return "XX";
  }
}

export function requiresConsent(country: string): boolean {
  // XX = unknown, T1 = Tor exit. Both are unresolved, so we treat them as
  // consent-required rather than assuming the visitor is elsewhere.
  return CONSENT_REGIONS.has(country) || country === "XX" || country === "T1";
}

let regionPromise: Promise<void> | null = null;

async function lookupCountry(): Promise<string> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    const response = await fetch("/cdn-cgi/trace", {
      signal: controller.signal,
      cache: "no-store",
    });
    clearTimeout(timer);
    if (!response.ok) return "XX";
    const trace = await response.text();
    const match = trace.match(/loc=([A-Za-z0-9]{2})/);
    return match ? match[1].toUpperCase() : "XX";
  } catch {
    return "XX";
  }
}

/** Resolves the visitor's region once per page visit and, where no choice is
 *  needed, records that so tags can run without a banner. */
export function ensureConsentResolved(): Promise<void> {
  if (regionPromise) return regionPromise;

  regionPromise = (async () => {
    const existing = readConsent();
    if (existing) return;

    const country = await lookupCountry();
    cacheCountry(country);

    if (requiresConsent(country)) {
      // No choice yet: tags stay blocked and the banner asks.
      return;
    }

    writeConsent({
      noticeVersion: NOTICE_VERSION,
      choice: null,
      region: "open",
      country,
      decidedAt: null,
      recordedAt: new Date().toISOString(),
    });
  })();

  return regionPromise;
}

// ---------------------------------------------------------------------------
// The stored choice
// ---------------------------------------------------------------------------

export function readConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (!parsed || typeof parsed !== "object" || !parsed.region) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(record: ConsentRecord): void {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    /* private mode / disabled storage — the choice still applies this visit */
  }
  // localStorage events only reach other tabs, so notify this one directly.
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
}

/** Read fresh on every send — a withdrawal must take effect immediately. */
export function canRunAdTags(): boolean {
  const record = readConsent();
  if (!record) return false; // unresolved, or awaiting a decision
  if (record.region === "open") return true;
  return record.choice === "accepted";
}

/** True while the visitor still has a decision to make. */
export function needsConsentPrompt(): boolean {
  const record = readConsent();
  if (record && (record.region === "open" || record.choice !== null)) return false;
  return requiresConsent(cachedCountry());
}

export function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown";
  }
}

// ---------------------------------------------------------------------------
// Recording choices
// ---------------------------------------------------------------------------

async function persistConsentRecord(
  choice: ConsentChoice,
  region: "consent_required" | "open",
  country: string,
): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    await supabase.from("consent_records").insert({
      user_id: data.session?.user?.id ?? null,
      visitor_id: getVisitorId(),
      choice,
      notice_version: NOTICE_VERSION,
      region,
      country_code: country,
      decided_at: new Date().toISOString(),
    });
  } catch {
    // A failed server record must never block the visitor's choice or the site.
  }
}

function recordChoice(choice: ConsentChoice): void {
  const previous = readConsent();
  const country = cachedCountry();
  const now = new Date().toISOString();

  writeConsent({
    noticeVersion: NOTICE_VERSION,
    choice,
    region: "consent_required",
    country,
    decidedAt: now,
    recordedAt: now,
    // Acceptance evidence is kept when a later withdrawal is recorded.
    previouslyAcceptedAt:
      previous?.previouslyAcceptedAt ??
      (previous?.choice === "accepted" ? previous.decidedAt ?? undefined : undefined),
    previouslyAcceptedVersion:
      previous?.previouslyAcceptedVersion ??
      (previous?.choice === "accepted" ? previous.noticeVersion : undefined),
  });

  void persistConsentRecord(choice, "consent_required", country);
}

export function acceptConsent(): void {
  recordChoice("accepted");
}

export function rejectConsent(): void {
  recordChoice("rejected");
}

/** The banner's "Save preferences": advertising on or off. */
export function savePreferences(advertising: boolean): void {
  if (advertising) acceptConsent();
  else rejectConsent();
}

/** Reopen the banner from anywhere (the footer's Cookie settings link). */
export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
}

/** React to a choice made in another tab. */
export function subscribeToConsentChanges(onChange: () => void): () => void {
  const handle = () => onChange();
  window.addEventListener(CONSENT_CHANGED_EVENT, handle);
  window.addEventListener("storage", handle);
  return () => {
    window.removeEventListener(CONSENT_CHANGED_EVENT, handle);
    window.removeEventListener("storage", handle);
  };
}
